import { plural, t } from "@lingui/core/macro";
import {
  isYesterday,
  isTomorrow,
  isToday,
  addDays,
  differenceInCalendarDays,
  startOfToday,
} from "date-fns";
import { humanFormat } from "@medoma/models/date-and-time";
import z from "zod";

const dateAndMonthFormatOptions = ({
  monthFormat,
}: {
  monthFormat: "long" | "numeric";
}) => ({
  month: monthFormat,
  day: "numeric" as const,
});

const dayDateAndMonthFormatOptions = ({
  monthFormat,
}: {
  monthFormat: "long" | "numeric";
}) => ({
  weekday: "long" as const,
  month: monthFormat,
  day: "numeric" as const,
});

const dateMonthAndYearFormatOptions = {
  day: "numeric" as const,
  month: "numeric" as const,
  year: "numeric" as const,
};

export const dateName = (
  date: Date,
  { monthFormat }: { monthFormat: "long" | "numeric" } = {
    monthFormat: "long",
  },
) => {
  const dateAndMonthString = humanFormat(
    date,
    dateAndMonthFormatOptions({ monthFormat }),
  );
  if (isToday(date)) {
    return `${t`Idag`}, ${dateAndMonthString}`;
  }

  if (isTomorrow(date)) {
    return `${t`Imorgon`}, ${dateAndMonthString}`;
  }

  if (isYesterday(date)) {
    return `${t`Igår`}, ${dateAndMonthString}`;
  }

  const dayDateAndMonth = humanFormat(
    date,
    dayDateAndMonthFormatOptions({ monthFormat }),
  );
  return dayDateAndMonth.charAt(0).toUpperCase() + dayDateAndMonth.slice(1);
};

export const dateAndMonth = (
  date: Date,
  { monthFormat }: { monthFormat: "long" | "numeric" } = {
    monthFormat: "long",
  },
) => {
  return humanFormat(date, dateAndMonthFormatOptions({ monthFormat }));
};

export const dateMonthAndYear = (date: Date) => {
  return humanFormat(date, dateMonthAndYearFormatOptions);
};

// "idag", "igår" or "3 dagar sedan", for a date today or in the past
export const daysAgo = (date: Date) => {
  const daysSinceDate = differenceInCalendarDays(startOfToday(), date);
  return plural(daysSinceDate, {
    0: "idag",
    1: "igår",
    other: "# dagar sedan",
  });
};

// Returns a new Date object for a selected date with time from a "HH:MM" or "HH:MM:SS" string and optional day offset (only uses the hours and minutes parts of the time string)
export const setTimeOnDate = ({
  selectedDate,
  time,
  addedDays,
}: {
  selectedDate: Date;
  time: string;
  addedDays: -1 | 0 | 1;
}) => {
  const parts = time.split(":");
  const hoursPart = z
    .string()
    .regex(/^[0-9]{2}$/)
    .parse(parts[0]);
  const minutesPart = z
    .string()
    .regex(/^[0-9]{2}$/)
    .parse(parts[1]);
  const hours = parseInt(hoursPart);
  const minutes = parseInt(minutesPart);
  const date = addDays(selectedDate, addedDays);
  date.setHours(hours, minutes);
  return date;
};
